import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Zap,
  Users,
  Target,
  Star,
  ChevronDown,
  ChevronUp,
  Lightbulb,
  BookOpen,
  MessageCircle
} from 'lucide-react';
import { RFFFlowDiagram } from '../components/RFFFlowDiagram';
import { ReadinessAssessment } from '../components/ReadinessAssessment';
import { useLanguage } from '../contexts/LanguageContext';

export const NewServicesPage = () => {
  const [expandedService, setExpandedService] = useState(null);
  const { t } = useLanguage();

  // Services data from translations
  const rawServices = t('servicesPage.services');
  const servicesMeta = [
    {
      id: 'change-management',
      icon: Zap,
      gradient: 'from-purple-600 to-blue-600'
    },
    {
      id: 'business-coaching',
      icon: Users,
      gradient: 'from-emerald-500 to-teal-600'
    },
    {
      id: 'scrum-for-sales',
      icon: Target,
      gradient: 'from-orange-500 to-red-500'
    },
    {
      id: 'intercultural-intelligence',
      icon: Star,
      gradient: 'from-violet-500 to-purple-600'
    }
  ];

  const services = Array.isArray(rawServices)
    ? servicesMeta.map((meta) => {
        const item =
          rawServices.find((s) => s.id === meta.id) || rawServices[0] || {};
        return {
          ...item,
          icon: meta.icon,
          gradient: meta.gradient
        };
      })
    : [];

  const toggleService = (serviceId) => {
    setExpandedService(expandedService === serviceId ? null : serviceId);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-24 pt-32 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 -left-20 w-96 h-96 bg-gradient-to-r from-purple-600/20 to-orange-500/20 rounded-full blur-3xl animate-float" />
          <div
            className="absolute bottom-1/4 -right-20 w-80 h-80 bg-gradient-to-r from-orange-500/20 to-purple-600/20 rounded-full blur-3xl animate-float"
            style={{ animationDelay: '-3s' }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-6xl font-light text-white mb-6">
            {t('servicesPage.hero.titleLine1')}
            <br />
            <span className="gradient-text font-medium">
              {t('servicesPage.hero.titleHighlight')}
            </span>
          </h1>

          <p className="text-xl text-slate-300 max-w-4xl mx-auto leading-relaxed font-light mb-8">
            {t('servicesPage.hero.subtitle')}
          </p>

          <div className="inline-flex items-center px-6 py-3 bg-white/10 backdrop-blur-xl border border-white/20 rounded-full">
            <Lightbulb className="w-5 h-5 text-orange-400 mr-2" />
            <span className="text-sm text-slate-200">
              {t('servicesPage.hero.badge')}
            </span>
          </div>
        </div>
      </section>

      {/* RFF Flow Diagram */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-light text-slate-900 mb-4">
              {t('servicesPage.rff.titleBeforeHighlight')}{' '}
              <span className="gradient-text font-medium">
                {t('servicesPage.rff.highlight')}
              </span>
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              {t('servicesPage.rff.subtitle')}
            </p>
          </div>
          <RFFFlowDiagram />
        </div>
      </section>

      {/* Services Detail - Accordion Style */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-light text-slate-900 mb-4">
              {t('servicesPage.servicesSection.titleBeforeHighlight')}{' '}
              <span className="gradient-text font-medium">
                {t('servicesPage.servicesSection.highlight')}
              </span>
            </h2>
            <p className="text-lg text-slate-600">
              {t('servicesPage.servicesSection.subtitle')}
            </p>
          </div>

          <div className="space-y-6">
            {services.map((service) => (
              <div
                key={service.id}
                className="border border-slate-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-300"
              >
                {/* Service Header - Clickable */}
                <button
                  onClick={() => toggleService(service.id)}
                  className="w-full p-6 flex items-center justify-between bg-white hover:bg-slate-50 transition-colors duration-200"
                >
                  <div className="flex items-center gap-4">
                    <div
                      className={`w-12 h-12 rounded-xl bg-gradient-to-r ${service.gradient} flex items-center justify-center`}
                    >
                      <service.icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-left">
                      <h3 className="text-xl font-semibold text-slate-900">
                        {service.title}
                      </h3>
                      <p
                        className={`text-sm bg-gradient-to-r ${service.gradient} bg-clip-text text-transparent font-medium`}
                      >
                        {service.subtitle}
                      </p>
                    </div>
                  </div>
                  {expandedService === service.id ? (
                    <ChevronUp className="w-6 h-6 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-6 h-6 text-slate-400" />
                  )}
                </button>

                {/* Service Content - Expandable */}
                {expandedService === service.id && (
                  <div className="p-8 bg-slate-50 border-t border-slate-200">
                    {/* Intro */}
                    <div className="mb-8 p-4 bg-white rounded-xl border-l-4 border-purple-600">
                      <p className="text-slate-700 italic leading-relaxed">
                        {service.intro}
                      </p>
                    </div>

                    {/* Description */}
                    <p className="text-lg text-slate-700 leading-relaxed mb-8">
                      {service.description}
                    </p>

                    {/* Approach - Phases */}
                    <div className="mb-8">
                      <h4 className="text-xl font-semibold text-slate-900 mb-6 flex items-center">
                        <BookOpen className="w-5 h-5 text-purple-600 mr-2" />
                        {t('servicesPage.serviceDetails.approachTitle')}
                      </h4>
                      <div className="space-y-6">
                        {Array.isArray(service.approach) &&
                          service.approach.map((phase, idx) => (
                            <div
                              key={idx}
                              className="bg-white p-6 rounded-xl border border-slate-200"
                            >
                              <h5 className="font-semibold text-slate-900 mb-2">
                                {t('servicesPage.serviceDetails.phaseLabel', {
                                  defaultValue: 'Phase'
                                })}{' '}
                                {idx + 1}: {phase.phase}
                              </h5>
                              <p className="text-slate-600 mb-4">
                                {phase.description}
                              </p>
                              <div className="grid md:grid-cols-2 gap-2">
                                {Array.isArray(phase.deliverables) &&
                                  phase.deliverables.map((item, i) => (
                                    <div
                                      key={i}
                                      className="flex items-start text-sm text-slate-600"
                                    >
                                      <div
                                        className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${service.gradient} mt-2 mr-2 flex-shrink-0`}
                                      />
                                      {item}
                                    </div>
                                  ))}
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>

                    {/* Why Us */}
                    <div className="mb-8 p-6 bg-gradient-to-r from-purple-50 to-orange-50 rounded-xl border border-purple-200">
                      <p className="text-slate-700 leading-relaxed">
                        <span className="font-semibold text-slate-900">
                          {t('servicesPage.serviceDetails.whyUsLabel')}{' '}
                        </span>
                        {service.whyUs}
                      </p>
                    </div>

                    {/* FAQ */}
                    {Array.isArray(service.faq) && service.faq.length > 0 && (
                      <div>
                        <h4 className="text-lg font-semibold text-slate-900 mb-4 flex items-center">
                          <MessageCircle className="w-5 h-5 text-orange-500 mr-2" />
                          {t('servicesPage.serviceDetails.faqTitle')}
                        </h4>
                        <div className="space-y-4">
                          {service.faq.map((faq, idx) => (
                            <div
                              key={idx}
                              className="bg-white p-4 rounded-lg border border-slate-200"
                            >
                              <p className="font-medium text-slate-900 mb-2">
                                {faq.q}
                              </p>
                              <p className="text-slate-600 text-sm">
                                {faq.a}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive Assessment */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <ReadinessAssessment />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-light text-slate-900 mb-6">
            {t('servicesPage.cta.titleBeforeHighlight')}{' '}
            <span className="gradient-text font-medium">
              {t('servicesPage.cta.highlight')}
            </span>
            ?
          </h2>
          <p className="text-lg text-slate-600 mb-8 leading-relaxed">
            {t('servicesPage.cta.text')}
          </p>
          <Link
            to="/kontakt"
            className="inline-flex items-center px-10 py-5 bg-gradient-to-r from-purple-600 to-orange-500 text-white rounded-full font-medium text-lg transition-all duration-300 hover:shadow-glow hover:scale-105"
          >
            <span className="mr-3">{t('servicesPage.cta.button')}</span>
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};
